import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import moment from 'moment';
import { TranslateService } from '@ngx-translate/core';
import { CalendarEvent, CalendarLocale, CalendarNavigateEvent, CalendarView } from 'ng-full-calendar';
import { ParentAcademicCalendarService, ParentAcademicEvent } from './parent-academic-calendar.service';

// Read-only parent Academic Calendar, reusing the exact same ngfc-calendar UI
// as the staff academic calendar (StaffAcademicCalendarComponent) — same
// month-view grid, [editable]="false", and a plain info popup in place of
// ngfc-calendar's built-in create/edit modal — wired to the parent-specific
// getEventInfo endpoint (classId+studentId, no staffId).
@Component({
  selector: 'app-parent-academic-calendar',
  templateUrl: './parent-academic-calendar.component.html',
  styleUrls: ['./parent-academic-calendar.component.css']
})
export class ParentAcademicCalendarComponent implements OnInit, OnChanges {
  @Input() studentId = '';
  @Input() classId = '';
  @Input() studentName = '';

  events: CalendarEvent[] = [];
  ngfcDate: Date = new Date();
  ngfcView: CalendarView = 'month';
  ngfcLocale: CalendarLocale | null = null;

  loadingData = true;
  private currentRangeKey = '';

  eventPopupVisible = false;
  selectedEvent: CalendarEvent | null = null;

  constructor(
    private parentAcademicCalendarService: ParentAcademicCalendarService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.setTranslatedCalendarLocale();
    this.loadEventsForVisibleMonth();
  }

  // A student switch (sibling tap) re-fetches the currently visible month
  // for the newly active student — currentRangeKey alone wouldn't catch
  // this since the date range itself doesn't change on a student switch.
  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['studentId'] || changes['classId']) && !changes['studentId']?.firstChange) {
      this.currentRangeKey = '';
      this.loadEventsForVisibleMonth();
    }
  }

  private setTranslatedCalendarLocale() {
    this.translate.get([
      'calendar.months',
      'calendar.monthsShort',
      'calendar.weekdays',
      'calendar.weekdaysShort'
    ]).subscribe(translated => {
      this.ngfcLocale = {
        weekdayNamesShort: translated['calendar.weekdaysShort'],
        weekdayNamesLong: translated['calendar.weekdays'],
        monthNamesShort: translated['calendar.monthsShort'],
        monthNamesLong: translated['calendar.months'],
        timeFormat: '12h',
      };
    });
  }

  // ngfc-calendar's own prev/next/today buttons (and any programmatic date
  // change) fire this — re-fetch only when the visible month actually
  // changed, not on every keystroke of internal state.
  onNavigate(navEvent: CalendarNavigateEvent) {
    this.ngfcDate = navEvent.date;
    this.loadEventsForVisibleMonth();
  }

  private async loadEventsForVisibleMonth() {
    if (!this.studentId || !this.classId) { this.loadingData = false; return; }
    const startDate = moment(this.ngfcDate).startOf('month').format('YYYY-MM-DD');
    const endDate = moment(this.ngfcDate).endOf('month').format('YYYY-MM-DD');
    const rangeKey = `${this.studentId}_${startDate}_${endDate}`;
    if (rangeKey === this.currentRangeKey) { return; }
    this.currentRangeKey = rangeKey;

    this.loadingData = true;
    try {
      const response = await firstValueFrom(
        this.parentAcademicCalendarService.getEventInfo(this.classId, this.studentId, startDate, endDate)
      );
      const eventList = response?.status ? (response.body?.eventList || []) : [];
      this.events = this.convertToCalendarEvents(eventList);
    } catch {
      this.events = [];
    } finally {
      this.loadingData = false;
    }
  }

  // Parses the API's ISO datetime as literal wall-clock numbers rather than
  // a real UTC instant, so ngfc-calendar (which reads Date via local-time
  // getters) doesn't shift the displayed time by the browser's timezone
  // offset — same approach as StaffAcademicCalendarComponent.parseApiDate.
  private parseApiDate(iso: string): Date {
    const [datePart, timePart] = iso.split('T');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hour, minute] = (timePart || '00:00').split(':').map(Number);
    return new Date(year, month - 1, day, hour, minute);
  }

  private isFullDayRange(start: Date, end: Date): boolean {
    const startsAtMidnight = start.getHours() === 0 && start.getMinutes() === 0;
    const endsAtMidnight = end.getHours() === 0 && end.getMinutes() === 0;
    const endsAtDayClose = end.getHours() === 23 && end.getMinutes() === 59;
    return startsAtMidnight && (endsAtMidnight || endsAtDayClose);
  }

  private convertToCalendarEvents(eventList: ParentAcademicEvent[]): CalendarEvent[] {
    return eventList.map((event, index) => {
      const start = this.parseApiDate(event.startDate);
      const end = event.endDate ? this.parseApiDate(event.endDate) : start;
      return {
        id: `${index}-${event.eventName}-${event.startDate}`,
        title: event.eventName,
        start,
        end,
        allDay: this.isFullDayRange(start, end),
        color: event.colorCode,
        description: event.description,
      };
    });
  }

  handleEventClick(event: CalendarEvent) {
    this.selectedEvent = event;
    this.eventPopupVisible = true;
  }

  closeEventPopup() {
    this.eventPopupVisible = false;
    this.selectedEvent = null;
  }
}
